import { isDefined, isNonEmpty, isNotDefined } from '@sgme/fp';
import { asIsoDate, userInputToIsoDate } from 'components/shared/DatePicker/utils';
import { formatDistanceToNow, formatISO, isToday, millisecondsToHours, startOfDay } from 'date-fns';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import { enGB } from 'date-fns/locale/en-GB';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import type { MessageFormatter } from 'locales/types';
import { useCallback } from 'react';
import type { Brand } from 'types/utils';
import { TIME_ZONE } from 'utils/core/date';

import { now } from './core/now';
export type IsoDateOnly = Brand<string, 'isoDateOnly'>;
export const nowISODateString = (): string => formatISO(now(), { representation: 'date' });
export function getDateOnlyFromIsoString(value?: string | null): Nullable<IsoDateOnly> {
  if (value === null || value === undefined) {
    return null;
  }

  // Remove time component as this is a date only value
  const dateOnlyPart = value?.split('T')[0];

  // do not take timezone into account for date-only values, otherwise anyone with a negative UTC offset (eg. New-York) will get yesterday date
  return dateOnlyPart as IsoDateOnly;
}

export function getDateOnlyFromDate(value: Date): IsoDateOnly {
  return getDateOnlyFromIsoString(asIsoDate(value))!;
}

export function parsePastedDate(text: string, dateFormat: string): string | undefined {
  return userInputToIsoDate(text, dateFormat);
}

export function parsePastedDateWithErrorMessage(text: string, dateFormat: string, formatError: MessageFormatter) {
  const parsedDate = userInputToIsoDate(text, dateFormat);
  if (parsedDate !== undefined) {
    return parsedDate;
  } else {
    const msg = formatError({ id: 'moreMenu.pasteFromClipboard.error.invalidDate' as IntlKey }, { text, dateFormat });
    throw new Error(msg);
  }
}

export const parseMultipleDates = (text: string): string[] => {
  return text
    .split(',')
    .map(x => x?.trim())
    .filter(isNonEmpty);
};

export const parsePastedDates = (cellContent: string, dateFormat: string) => {
  return parseMultipleDates(cellContent)
    .map(text => parsePastedDate(text, dateFormat))
    .filter(isDefined);
};

export const parsePastedDatesWithErrorMessage = (
  cellContent: string,
  dateFormat: string,
  formatError: MessageFormatter,
) => {
  return parseMultipleDates(cellContent).map(text => parsePastedDateWithErrorMessage(text, dateFormat, formatError));
};

export const displayDateFormatOptions = {
  year: 'numeric' as const,
  month: 'short' as const,
  day: 'numeric' as const,
};

const dateTimeFormatOptions = {
  year: 'numeric' as const,
  month: 'short' as const,
  day: 'numeric' as const,
  hour: 'numeric' as const,
  minute: 'numeric' as const,
  second: 'numeric' as const,
};

export function useFormatDateTime() {
  const { formatDate, formatTime } = useSPIntl();
  return useCallback(
    (value?: string) => {
      if (isNotDefined(value)) {
        return '';
      }
      const dateValue = new Date(value);
      if (isToday(dateValue)) {
        return formatTime(dateValue, {
          hour: 'numeric' as const,
          minute: 'numeric' as const,
          second: 'numeric' as const,
        });
      }
      return formatDate(dateValue, dateTimeFormatOptions);
    },
    [formatDate, formatTime],
  );
}
export type HourMinuteTime = `${number}${number}:${number}${number}`;

export function formatTimeFromTimezoneToTimezone(
  time: HourMinuteTime,
  srcTimeZone: string,
  destinationTimezone: string,
  anchorDay?: Date,
) {
  const [hours, minutes] = time.split(':').map(v => parseInt(v, 10));
  if (isNaN(hours) || isNaN(minutes)) {
    return;
  }
  const today = startOfDay(anchorDay ?? now());

  const todayOffset = millisecondsToHours(getTimezoneOffset(TIME_ZONE));
  const sourceTimeZoneOffset = millisecondsToHours(getTimezoneOffset(srcTimeZone)) * -1;
  today.setHours(hours + sourceTimeZoneOffset + todayOffset);
  today.setMinutes(minutes);
  return formatInTimeZone(today, destinationTimezone, 'HH:mm zzz', { locale: enGB });
}

export const useFormatToRelativeTime = (dateString: string): string => {
  const { formatMessage } = useSPIntl();

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return '';
  }

  const diffInSeconds = (Date.now() - date.getTime()) / 1000;

  if (diffInSeconds < 60) {
    return formatMessage({ id: 'utils.now' as IntlKey });
  }

  return formatMessage({ id: 'utils.relativeTimeFromNow' as IntlKey }, { relativeTime: formatDistanceToNow(date) });
};
